/**
 * Visitors tab — live table of today's visitors with check-in/out.
 * Adapted from AIM VisitorDashboard for SMS context.
 */
import { useMemo, useState } from 'react';
import { Box, Center, Flex, Input, Spinner, Text } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import {
  DataTable,
  StatusBadge,
  Button as ButtonComponent,
  SelectInput,
  type Column,
  type StatusType,
} from '@themes/aegix';
import {
  useActiveVisits,
  useTodayVisits,
  useCheckOut,
} from '../../hooks/queries/useVisitorManagement';
import type { VisitListItem, VisitStatus } from '../../types/VisitorManagementTypes';
import { toaster } from '../ui/toaster';
import { useVisitorPermissions } from '../../hooks/useVisitorPermissions';
import { useVisitorSiteScope } from './hooks/useVisitorSiteScope';

type ViewMode = 'active' | 'today';

// STATUS_MAP is built inside the component so labels react to language changes.

function formatTime(dateStr: string | null): string {
  if (!dateStr) return '\u2014';
  return new Date(dateStr).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

interface VisitorRow {
  [key: string]: unknown;
  id: string;
  badge_number: string;
  visitor_name: string;
  status: VisitStatus;
  reason: string;
  host_name: string;
  destination: string;
  checked_in_at: string;
  checked_out_at: string | null;
}

export function VisitorsContent() {
  const { t } = useTranslation();
  const [viewMode, setViewMode] = useState<ViewMode>('active');

  const STATUS_MAP = useMemo<Record<VisitStatus, { status: StatusType; label: string }>>(
    () => ({
      checked_in: { status: 'active' as StatusType, label: t('visitors.status.checkedIn', 'Checked In') },
      checked_out: { status: 'inactive' as StatusType, label: t('visitors.status.checkedOut', 'Checked Out') },
      flagged: { status: 'error' as StatusType, label: t('visitors.status.flagged', 'Flagged') },
      denied: { status: 'error' as StatusType, label: t('visitors.status.denied', 'Denied') },
    }),
    [t],
  );
  const [searchFilter, setSearchFilter] = useState('');
  const { canParticipate, isLoading: permissionsLoading } = useVisitorPermissions();

  const { siteId, setSiteId, siteOptions } = useVisitorSiteScope();

  const { data: activeData, isLoading: activeLoading } = useActiveVisits();
  const { data: todayData, isLoading: todayLoading } = useTodayVisits(siteId);
  const checkOutMutation = useCheckOut();

  const visits: VisitListItem[] = useMemo(() => {
    const raw = viewMode === 'active' ? activeData : todayData;
    return Array.isArray(raw) ? raw : [];
  }, [viewMode, activeData, todayData]);

  const filteredVisits = useMemo(() => {
    if (!searchFilter) return visits;
    const q = searchFilter.toLowerCase();
    return visits.filter(
      (v) =>
        v.visitor_name?.toLowerCase().includes(q) ||
        v.badge_number?.toLowerCase().includes(q) ||
        v.host_name?.toLowerCase().includes(q) ||
        v.destination?.toLowerCase().includes(q),
    );
  }, [visits, searchFilter]);

  const rows: VisitorRow[] = filteredVisits.map((v) => ({
    id: v.public_id,
    badge_number: v.badge_number,
    visitor_name: v.visitor_name,
    status: v.status,
    reason: v.reason_type ?? '',
    host_name: v.host_name,
    destination: v.destination,
    checked_in_at: v.checked_in_at,
    checked_out_at: v.checked_out_at,
  }));

  const handleCheckOut = (publicId: string) => {
    checkOutMutation.mutate(
      { visit_public_id: publicId },
      {
        onSuccess: () => toaster.create({ title: t('visitors.checkedOut', 'Visitor checked out'), type: 'success' }),
        onError: (err) =>
          toaster.create({
            title: t('visitors.checkOutFailed', 'Check-out failed'),
            description: err instanceof Error ? err.message : t('common.unknownError', 'Unknown error'),
            type: 'error',
          }),
      },
    );
  };

  const columns = useMemo<Column<VisitorRow>[]>(() => [
    { key: 'badge_number', label: t('visitors.col.badge', 'Badge'), width: '90px' },
    { key: 'visitor_name', label: t('visitors.col.name', 'Name'), sortable: true, width: '180px' },
    {
      key: 'status',
      label: t('visitors.col.status', 'Status'),
      width: '120px',
      render: (row) => {
        const mapped = STATUS_MAP[row.status];
        return mapped ? (
          <StatusBadge status={mapped.status} label={mapped.label} />
        ) : null;
      },
    },
    { key: 'reason', label: t('visitors.col.reason', 'Reason'), width: '140px' },
    { key: 'host_name', label: t('visitors.col.host', 'Host'), width: '140px' },
    { key: 'destination', label: t('visitors.col.destination', 'Destination'), width: '140px' },
    {
      key: 'checked_in_at',
      label: t('visitors.col.in', 'In'),
      width: '80px',
      render: (row) => <Text fontSize="sm">{formatTime(row.checked_in_at)}</Text>,
    },
    {
      key: 'checked_out_at',
      label: t('visitors.col.out', 'Out'),
      width: '80px',
      render: (row) => <Text fontSize="sm">{formatTime(row.checked_out_at)}</Text>,
    },
    {
      key: 'actions',
      label: '',
      width: '100px',
      render: (row) =>
        row.status === 'checked_in' && !permissionsLoading && canParticipate ? (
          <ButtonComponent
            visual="secondary"
            size="sm"
            onClick={(e: React.MouseEvent) => {
              e.stopPropagation();
              handleCheckOut(row.id);
            }}
           data-e2e="check-out-button">
            {t('visitors.checkOut', 'Check Out')}
          </ButtonComponent>
        ) : null,
    },
  ], [t, STATUS_MAP, permissionsLoading, canParticipate, handleCheckOut]);

  const isLoading = viewMode === 'active' ? activeLoading : todayLoading;

  return (
    <Box>
      <Flex px={5} pt={4} pb={2} justify="space-between" align="center">
        <Text fontSize="sm" color="text.secondary">
          {viewMode === 'active'
            ? t('visitors.activeCount', '{{count}} active visitor', { count: filteredVisits.length })
            : t('visitors.todayCount', '{{count}} visit today', { count: filteredVisits.length })}
        </Text>
        <Flex gap={3} align="center">
          {viewMode === 'today' && siteOptions.length > 1 && (
            <SelectInput
              value={siteId == null ? '' : String(siteId)}
              name="visitor-today-site"
              label={t('common.school', 'School')}
              option={siteOptions}
              onChange={(val: string) => setSiteId(Number(val))}
              e2eTitle="visitors-today-site-select"
            />
          )}
          <Input
            placeholder={t('visitors.searchPlaceholder', 'Search visitors...')}
            aria-label={t('visitors.searchPlaceholder', 'Search visitors...')}
            value={searchFilter}
            onChange={(e) => setSearchFilter(e.target.value)}
            maxW="250px"
            size="sm"
          />
          <ButtonComponent
            visual={viewMode === 'active' ? 'primary' : 'secondary'}
            size="sm"
            onClick={() => setViewMode('active')}
           data-e2e="active-button">
            {t('visitors.viewActive', 'Active')}
          </ButtonComponent>
          <ButtonComponent
            visual={viewMode === 'today' ? 'primary' : 'secondary'}
            size="sm"
            onClick={() => setViewMode('today')}
           data-e2e="today-button">
            {t('visitors.viewToday', 'Today')}
          </ButtonComponent>
        </Flex>
      </Flex>

      <Box px={5} pb={5}>
        {isLoading ? (
          <Center py={12}>
            <Spinner size="lg" color="button.primary" />
          </Center>
        ) : rows.length === 0 ? (
          <Center py={12}>
            <Text color="text.secondary">
              {searchFilter
                ? t('visitors.noMatchSearch', 'No visitors match your search')
                : viewMode === 'active'
                  ? t('visitors.noActive', 'No active visitors right now')
                  : t('visitors.noToday', 'No visits recorded today')}
            </Text>
          </Center>
        ) : (
          <DataTable
            columns={columns}
            data={rows}
            keyExtractor={(row) => row.id}
          />
        )}
      </Box>
    </Box>
  );
}
